import { FacebookService, InitParams } from 'ngx-facebook';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  Component,
  OnDestroy,
  ChangeDetectorRef,
  AfterViewInit,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { SnAbilityService } from '@lib/ability';
import { SnWidget } from '@lib/api/entities';
import { SnOperationalViewsAPI } from '@lib/api/services';
import { SnFormFieldConfig } from '@lib/form';
import { t } from '@lib/translation';

@Component({
  selector: 'sn-facebook-live-widget',
  templateUrl: './facebook-live-widget.component.html',
  styleUrls: ['./facebook-live-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FacebookLiveWidgetComponent implements OnDestroy, AfterViewInit {
  @Input() public viewId: string;
  @Input() public widget: SnWidget;

  public isLoading: boolean = false;
  public videoSrc: string;
  public videoWidth: number;
  public options = [
    {
      label: t('dashboard.operational.widgets.small'),
      value: '400px',
    },
    {
      label: t('common.medium'),
      value: '500px',
    },
    {
      label: t('dashboard.operational.widgets.large'),
      value: '600px',
    },
    {
      label: t('dashboard.operational.widgets.extraLarge'),
      value: '700px',
    },
  ];
  public changeTitle = false;
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public formModel = {};
  public formFields: SnFormFieldConfig[] = [
    {
      key: 'videoUrl',
      type: 'input',
      templateOptions: {
        appearance: 'outline',
        label: t('dashboard.operational.widgets.facebookVideoUrlLabel'),
        required: true,
      },
    },
  ];
  public readonly canChange: boolean;
  private _initParams: InitParams = {
    xfbml: true,
    version: 'v2.8',
  };
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _facebookService: FacebookService,
    private readonly _operationalViewsAPI: SnOperationalViewsAPI,
    private readonly _cd: ChangeDetectorRef,
    private readonly _ability: SnAbilityService,
  ) {
    this.canChange = this._ability.can({ subject: 'dashboards/widgetobject', action: 'change' });
  }

  public ngAfterViewInit(): void {
    this._checkForUrl();
    this._cd.detectChanges();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public changeSrc(): void {
    this.videoSrc = null;
    this._cd.detectChanges();
  }

  public submitForm(): void {
    this.isLoading = true;
    const formData = this.form.value;
    this._operationalViewsAPI
      .updateViewWidget(this.viewId, this.widget.id, {
        meta: {
          videoUrl: formData.videoUrl,
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe((widget) => {
        this.widget.meta.videoUrl = widget.meta.videoUrl;
        this.videoSrc = this.widget.meta.videoUrl;
        this._facebookService.init(this._initParams);
        this.isLoading = false;
        this._cd.detectChanges();
      });
  }

  public editTitle(): void {
    this.changeTitle = true;
  }

  public setTitle(value: boolean): void {
    this.changeTitle = value;
    this._cd.detectChanges();
  }

  public setUpdateTitle(value: string): void {
    this.widget.name = value;
    this._cd.detectChanges();
  }

  private _checkForUrl(): void {
    this.isLoading = true;
    if (this.widget.meta.videoUrl) {
      this.videoSrc = this.widget.meta.videoUrl;
      this._facebookService.init(this._initParams);
      this.isLoading = false;
    } else {
      this.isLoading = false;
    }
    this._cd.detectChanges();
  }
}
